import React, { useState, useEffect } from "react";
import cancelIcon from "../../../assets/images/cancel-btn.svg";
import citiesData from "../../../components/cities.json";
import "./AddFamilyMemberModal.css";

const AddFamilyMemberModal = ({
  show,
  onClose,
  addPatientObject,
  setAddPatientObject,
  months,
  daysarray,
  years,
  popupBtnDis,
  onSubmit
}) => {
  const [communesList, setCommunesList] = useState([]);
  const [wilayasList, setWilayasList] = useState([]);

  // Load unique wilayas on component mount
  useEffect(() => {
    const uniqueWilayas = [];
    const wilayaMap = new Map();
    
    citiesData.forEach(city => {
      if (!wilayaMap.has(city.wilaya_code)) {
        wilayaMap.set(city.wilaya_code, {
          id: city.wilaya_code,
          code: city.wilaya_code,
          name: city.wilaya_name_ascii,
          name_ar: city.wilaya_name
        });
      }
    });
    
    setWilayasList(Array.from(wilayaMap.values()));
  }, []);

  // Load communes when wilaya is selected
  useEffect(() => {
    if (addPatientObject?.birthCity) {
      const communes = citiesData
        .filter(city => city.wilaya_code === addPatientObject.birthCity)
        .map(city => ({
          id: city.id,
          name: city.commune_name_ascii,
          name_ar: city.commune_name,
          wilaya_code: city.wilaya_code
        }));
      
      const uniqueCommunes = Array.from(
        new Map(communes.map(c => [c.id, c])).values()
      );
      
      setCommunesList(uniqueCommunes);
      
      // Reset birthCommune when wilaya changes
      if (addPatientObject?.birthCommune) {
        const communeExists = uniqueCommunes.some(c => c.id === addPatientObject.birthCommune);
        if (!communeExists) {
          setAddPatientObject(prev => ({...prev, birthCommune: ""}));
        }
      }
    } else {
      setCommunesList([]);
      setAddPatientObject(prev => ({...prev, birthCommune: ""}));
    }
  }, [addPatientObject?.birthCity]);

  if (!show) return null;

  return (
    <div className="popup holidayPopup addFamilyMemberModal">
      <div className="popup-content">
        <div className="addNursePopup addAppointment">
          <div className="modal-header-wrapper">
            <h2 className="nurseHdn">Add Family Member</h2>
            <button
              onClick={onClose}
              className="cancelBtn"
              aria-label="Close modal">
              <img src={cancelIcon} alt="Close" />
            </button>
          </div>
          
          <div className="patientAddBox">
            <ul className="addPatientList">
              {/* Gender Selection */}
              <li className="halfList">
                <div className="radioWithLabel">
                  <div
                    className={`radioWithLabelInner ${addPatientObject?.gender === "male" ? "active" : ""}`}
                    onClick={() => setAddPatientObject(prev => ({...prev, gender: "male"}))}>
                    <input type="radio" name="childGender" checked={addPatientObject?.gender === "male"} readOnly />
                    <label>Male</label>
                  </div>
                </div>
              </li>
              <li className="halfList">
                <div className="radioWithLabel">
                  <div
                    className={`radioWithLabelInner ${addPatientObject?.gender === "female" ? "active" : ""}`}
                    onClick={() => setAddPatientObject(prev => ({...prev, gender: "female"}))}>
                    <input type="radio" name="childGender" checked={addPatientObject?.gender === "female"} readOnly />
                    <label>Female</label>
                  </div>
                </div>
              </li>

              {/* Name Fields */}
              <li className="halfList">
                <label>First Name</label>
                <input
                  type="text"
                  placeholder="Enter first name (in French)"
                  value={addPatientObject?.firstName || ""}
                  onChange={e => setAddPatientObject(prev => ({...prev, firstName: e.target.value}))}
                />
              </li>
              <li className="halfList">
                <label>Last Name</label>
                <input
                  type="text"
                  placeholder="Enter last name (in French)"
                  value={addPatientObject?.lastName || ""}
                  onChange={e => setAddPatientObject(prev => ({...prev, lastName: e.target.value}))}
                />
              </li>

              {/* Date of Birth */}
              <li className="dateOfBirthCont">
                <label>Date of Birth</label>
                <ul>
                  <li>
                    <select 
                      value={addPatientObject?.month || ""} 
                      onChange={e => setAddPatientObject(prev => ({...prev, month: e.target.value}))}>
                      <option value="" disabled>Month</option>
                      {months?.map((item, index) => (
                        <option key={index} value={index + 1 < 10 ? "0" + (index + 1) : index + 1}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </li>
                  <li>
                    <select 
                      value={addPatientObject?.day || ""} 
                      onChange={e => setAddPatientObject(prev => ({...prev, day: e.target.value}))}>
                      <option value="" disabled>Day</option>
                      {daysarray?.map((item, index) => (
                        <option key={index} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                  </li>
                  <li>
                    <select 
                      value={addPatientObject?.year || ""} 
                      onChange={e => setAddPatientObject(prev => ({...prev, year: e.target.value}))}>
                      <option value="" disabled>Year</option>
                      {years?.map((year, index) => (
                        <option key={index} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </li>
                </ul>
              </li>

              {/* Birth Location */}
              <li className="halfList">
                <label>Birth Wilaya</label>
                <select 
                  value={addPatientObject?.birthCity || ""} 
                  onChange={e => setAddPatientObject(prev => ({...prev, birthCity: e.target.value, birthCommune: ""}))}>
                  <option value="" disabled>Select birth wilaya</option>
                  {wilayasList?.map((item, index) => (
                    <option key={index} value={item?.code}>
                      {item?.name}
                    </option>
                  ))}
                </select>
              </li>
              <li className="halfList">
                <label>Birth Commune</label>
                <select 
                  value={addPatientObject?.birthCommune || ""} 
                  onChange={e => setAddPatientObject(prev => ({...prev, birthCommune: e.target.value}))}
                  disabled={!addPatientObject?.birthCity}>
                  <option value="" disabled>
                    {!addPatientObject?.birthCity ? "Select wilaya first" : "Select birth commune"}
                  </option>
                  {communesList?.map((item, index) => (
                    <option key={index} value={item?.id}>
                      {item?.name}
                    </option>
                  ))}
                </select>
              </li>

              {/* Relationship */}
              <li>
                <label>Relationship With You</label>
                <select 
                  value={addPatientObject?.relation || ""} 
                  onChange={e => setAddPatientObject(prev => ({...prev, relation: e.target.value}))}>
                  <option value="" disabled hidden>Choose Relation</option>
                  <option value="mother">I am his/her mother</option>
                  <option value="father">I am his/her father</option>
                  <option value="family">Family member</option>
                </select>
              </li>

              {/* Submit Button */}
              <li>
                <button 
                  disabled={popupBtnDis} 
                  onClick={onSubmit}>
                  {popupBtnDis ? "Please wait..." : "Add"}
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFamilyMemberModal;
